import type { components } from '../types/api.generated';
import type { UserRoleSlug } from '../auth/roles';
import api from '../utils/api';

/**
 * The permission editor's API module (P6) — the three P4 routes plus P6's catalog
 * read, shaped like `reminderService.ts`: named types exported beside the
 * functions, consumed by the screen.
 *
 * ⚠ THIS IS THE FIRST FILE IN `frontend/src` TO IMPORT `types/api.generated.ts`
 * (verified at HEAD: the only other mention in the tree is a COMMENT at
 * `pages/Settings/UserManagement.tsx:67`). That is deliberate and it imports
 * exactly ONE thing — the 30-slug literal union off the PUT REQUEST body, which
 * the generator really does emit (`api.generated.ts:5449-5456`, the union on
 * `:5451`). Everything else here is hand-typed on purpose: all three permission
 * 200s are pinned `@ApiResponse({ status: 200, type: Object })` for INV-85
 * (`user-permissions.controller.ts:41-47`), so the generated RESPONSE types are
 * `Record<string, never>` and carry no keys at all. Do not "fix" that by casting
 * through them — the generator's own header says not to.
 *
 * What the one import buys: when slug #31 ships, `PERMISSION_NOTES` in
 * `pages/Settings/permissionNotes.ts` stops compiling until somebody writes its
 * sentence. Re-typing the union here would have bought silence instead.
 */
export type PermissionSlug =
  components['schemas']['UserPermissionOverrideInputDto']['permission'];

/** One stored divergence from the role defaults, as `GET` returns it. */
export interface UserPermissionOverrideRow {
  permission: PermissionSlug;
  enabled: boolean;
  /**
   * The acting user's `User.id` — an ID, never a name, and with no join
   * (`user-permissions.service.ts:99`). The backend types it NON-null
   * (`:33`; column `updatedBy String`, schema.prisma:1278); widened here because
   * the screen resolves it against the loaded roster and must render `—` for an
   * actor it cannot find (a deleted or foreign-clinic actor) either way.
   */
  updatedBy: string | null;
  /** An INSTANT. Render ONLY through `useFormatInstant` — never `new Date(x).toLocale*`. */
  updatedAt: string;
}

/** `GET /users/:id/permissions` — what the role gives, what diverges, what the guards enforce. */
export interface UserPermissionsView {
  userId: string;
  role: UserRoleSlug;
  defaults: PermissionSlug[];
  overrides: UserPermissionOverrideRow[];
  effective: PermissionSlug[];
}

export interface PermissionOverrideInput {
  permission: PermissionSlug;
  enabled: boolean;
}

/**
 * One row of `GET /permissions/catalog` — WELDED FACTS only (design §B). All
 * prose lives on the web in `pages/Settings/permissionNotes.ts` so it can be
 * re-worded without a backend release.
 */
export interface PermissionCatalogEntry {
  slug: PermissionSlug;
  label: string;
  /** Gates nothing at HEAD: a grant or revoke changes nothing. Machine-derived. */
  notEnforced: boolean;
  /** The slug this one is useless without (design decision #50). */
  requiresView: PermissionSlug | null;
  /** Re-added at resolution for every OWNER target, so the switch renders locked. */
  ownerLocked: boolean;
  /** Named anywhere in `SYNC_ENTITY_PERMISSIONS`: narrowing it can strand offline work. */
  affectsOfflineSync: boolean;
}

export interface PermissionCatalog {
  categories: { category: string; permissions: PermissionCatalogEntry[] }[];
  roleDefaults: Record<UserRoleSlug, PermissionSlug[]>;
}

export const userPermissionsKey = (userId: string) =>
  ['user-permissions', userId] as const;

export const PERMISSION_CATALOG_KEY = ['permission-catalog'] as const;

export async function getUserPermissions(userId: string): Promise<UserPermissionsView> {
  const r = await api.get<UserPermissionsView>(`/users/${userId}/permissions`);
  return r.data;
}

/**
 * ⚠ FULL REPLACEMENT. `overrides` must be the COMPLETE desired set every time —
 * a slug omitted from the body reverts to the role default
 * (`dto/user-permissions.dto.ts:24-28`), so building the body from "the switch the
 * user just clicked" silently clears every other customization. Build it with
 * `permissionEditorModel.toOverrides(state, view, catalog)`, which is state-based
 * for exactly this reason.
 *
 * `[]` is the legitimate reset shape and is how "Reset to role defaults" saves —
 * `DELETE /users/:id/permissions` is the same service code path with `[]`
 * (`user-permissions.controller.ts:70-80`), so this module exports no delete.
 *
 * Returns the post-commit view, so the caller needs no follow-up GET.
 */
export async function putUserPermissions(
  userId: string,
  overrides: PermissionOverrideInput[],
): Promise<UserPermissionsView> {
  const r = await api.put<UserPermissionsView>(`/users/${userId}/permissions`, {
    overrides,
  });
  return r.data;
}

/** Facts only — labels, categories, the four welded flags and every role's defaults. */
export async function getPermissionCatalog(): Promise<PermissionCatalog> {
  const r = await api.get<PermissionCatalog>('/permissions/catalog');
  return r.data;
}
